import type {
  CompositionHeadcountOnlyV3Query,
  CompositionV3BreakdownOptions,
  CompositionV3DataQuery,
  CompositionV3Ent,
} from '@revelio/data-access';

import type { Filter, PrimaryFilterId } from '../../../filters/types/filters';
import type { DataProviderQueryStatus } from '../types';
import { useMonthRefs } from '../utils/useDateRefs';
import { useCompositionRawData } from './CompositionRawDataProvider';

export type UseCompositionDataOptions = {
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV3Ent[];
  additionalEffectiveFilters?: Filter[];
  breakdownOptionsPatch?: Partial<CompositionV3BreakdownOptions>;
  requireFullMetrics?: boolean;
  pause?: boolean;
};

type FullCompositionDataResult = {
  compositionData: CompositionV3DataQuery['composition_v3'] | undefined;
  monthRefs: ReadonlyMap<number, string>;
  rawMonthRefs: string[];
  status: DataProviderQueryStatus;
};

type CompositionDataResult = {
  compositionData:
    | CompositionV3DataQuery['composition_v3']
    | CompositionHeadcountOnlyV3Query['composition_v3']
    | undefined;
  monthRefs: ReadonlyMap<number, string>;
  rawMonthRefs: string[];
  status: DataProviderQueryStatus;
};

export function useCompositionData(
  primaryDimension: PrimaryFilterId,
  options: UseCompositionDataOptions & { requireFullMetrics: false }
): CompositionDataResult;
export function useCompositionData(
  primaryDimension: PrimaryFilterId,
  options?: UseCompositionDataOptions & { requireFullMetrics?: true }
): FullCompositionDataResult;
export function useCompositionData(
  primaryDimension: PrimaryFilterId,
  {
    isTimeseries = true,
    entitiesOverride,
    additionalEffectiveFilters,
    breakdownOptionsPatch,
    requireFullMetrics = true,
    pause = false,
  }: UseCompositionDataOptions = {}
): CompositionDataResult {
  const {
    data,
    monthRefs: rawMonthRefs = [],
    status,
  } = useCompositionRawData({
    primaryDimension,
    isTimeseries,
    entitiesOverride,
    additionalEffectiveFilters,
    breakdownOptionsPatch,
    requireFullMetrics,
    pause,
  });
  const { monthRefs } = useMonthRefs();

  return {
    compositionData: data?.composition_v3,
    monthRefs,
    rawMonthRefs,
    status,
  };
}
